'use client'

import { Event, Team, RoundDetails } from '@/types'
import { Session } from 'next-auth'
import React, { useEffect, useRef } from 'react'
import { EventActions, PageBottomJoinCTA } from '../EventActions'
import SocialMedia from '../SocialMedia'
import { TabContents, TabContentsHandle } from '../TabContents'
import { EventStats } from '../EventStats'
import Image from 'next/image'
import { Badge } from 'flowbite-react'
import { DEFAULT_THUMBNAIL } from '@/constants/common'
import { toast } from '@/lib/utils/toast'
import { useSearchParams } from 'next/navigation'
import { DetailPageHeader } from '@/components/event-detail'

interface MainContainerProps {
  event: Event
  eventRound: RoundDetails | undefined
  team: Team | undefined
  rounds: RoundDetails[]
  session?: Session
}

export const MainContainer = ({
  event,
  eventRound,
  team,
  rounds,
  session,
}: MainContainerProps) => {
  const tabContentsRef = useRef<TabContentsHandle>(null)
  const searchParams = useSearchParams()

  const userId = session?.user.id
  const currentEventUrl = `/web/events/${event.slug}`

  const handleTabSelection = (value: number) => {
    tabContentsRef.current?.setActiveTab(value)
    const element = document.getElementById('event-content')
    element?.scrollIntoView({ behavior: 'smooth' })
  }

  // Show success toast after redirect from registration (robust dedupe: ref + sessionStorage)
  const hasShownToastRef = useRef(false)
  // Capture the initial joined query value once to avoid effect re-firing due to searchParams changes
  const initialJoinedRef = useRef<string | null>(searchParams.get('joined'))
  useEffect(() => {
    const joined = initialJoinedRef.current
    if (joined !== '1') return

    const key = `joined_toast_shown_${event.slug}`
    if (!hasShownToastRef.current && !sessionStorage.getItem(key)) {
      hasShownToastRef.current = true
      sessionStorage.setItem(key, '1')
      toast(`Successfully joined ${event.title}!`, 'success')
    }
    // Always strip ?joined=1 — even when the toast was deduped above —
    // otherwise the param lingers in the URL indefinitely. Uses the native
    // History API, NOT router.replace(): in the App Router, router.replace()
    // is a real navigation that re-fetches the Server Component tree;
    // history.replaceState() only changes the address bar, with no server
    // round-trip.
    window.history.replaceState(null, '', `/web/events/${event.slug}`)
  }, [event.slug, event.title])

  const hostInfo = (
    <>
      <div className="flex md:items-center justify-start gap-4 mt-4 mb-0">
        <div className="text-gray-700">Hosted by</div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 md:flex md:flex-wrap gap-3">
          <div className="flex items-center gap-2">
            {event.host_name && (
              <span className="font-semibold">{event.host_name}</span>
            )}
            <Image
              src={event.host_logo || DEFAULT_THUMBNAIL}
              alt={event.host_name || ''}
              width="62"
              height="24"
              style={{
                height: '24px',
                width: 'auto',
                maxWidth: '62px',
                objectFit: 'contain',
              }}
            />
          </div>
          <div className="flex items-center gap-1">
            {event.cohost_name && (
              <span className="font-semibold">{event.cohost_name}</span>
            )}
            {event.cohost_logo && (
              <Image
                src={event.cohost_logo || DEFAULT_THUMBNAIL}
                alt={event.cohost_name || ''}
                width="62"
                height="24"
                style={{
                  height: '24px',
                  width: 'auto',
                  maxWidth: '62px',
                  objectFit: 'contain',
                }}
              />
            )}
          </div>
        </div>
      </div>
      {event.organization?.name && event.organization?.logo_url && (
        <div className="flex items-center justify-start gap-3 mt-4">
          <span>Organized by</span>
          <div className="flex items-center gap-1">
            <span className="font-bold">{event.organization.name}</span>
            <Image
              src={event.organization.logo_url || DEFAULT_THUMBNAIL}
              alt=""
              width="62"
              height="24"
              style={{
                height: '24px',
                width: 'auto',
                maxWidth: '62px',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
      )}
    </>
  )

  const actions = (
    <>
      <EventActions
        currentEventUrl={currentEventUrl}
        event={event}
        userId={userId}
        eventRound={eventRound}
        selectTab={(value: number) => handleTabSelection(value)}
      />
      <SocialMedia event={event} />
    </>
  )

  const sidebar = (
    <div className="flex flex-col w-[355px]">
      <div className="overflow-hidden w-full">
        <Image
          src={event.thumbnail_url || DEFAULT_THUMBNAIL}
          alt=""
          width="355"
          height="192"
          sizes="100vw"
          className="rounded-lg"
          style={{
            width: '100%',
            height: 'auto',
            objectFit: 'contain',
            maxHeight: '192px',
          }}
        />
      </div>
      <p className="text-gray-800 mt-4">{event.description}</p>
      <div className="flex flex-wrap gap-2 mt-4">
        {event.tag_list &&
          event.tag_list.map((tag, index) => (
            <Badge
              key={index}
              className="px-2.5 py-0.5 bg-sky-100 text-neutral-500 rounded-md"
            >
              <span className="text-blue-800 text-xs font-medium">{tag}</span>
            </Badge>
          ))}
      </div>
    </div>
  )

  return (
    <>
      <DetailPageHeader
        title={event.title}
        hostInfo={hostInfo}
        actions={actions}
        sidebar={sidebar}
      />

      <div className="bg-gray-50 px-4 md:px-8 lg:px-0 py-10 md:pb-16">
        <div className="2xl:max-w-[1152px] 2xl:m-auto">
          <EventStats event={event} />
        </div>
      </div>

      <div id="event-content" className="2xl:max-w-[1152px] 2xl:m-auto">
        <section className="px-4 md:px-8 lg:px-16 py-10 lg:py-16">
          <TabContents
            ref={tabContentsRef}
            event={event}
            team={team}
            rounds={rounds}
            session={session ?? undefined}
          />
          <PageBottomJoinCTA
            event={event}
            currentEventUrl={currentEventUrl}
            userId={userId}
          />
        </section>
      </div>
    </>
  )
}
